/**
 * PickTradesDialog
 *
 * "Pick trades": where a user tells JAI about draft picks they traded. Saving
 * changes the draft order JAI uses (pick list, Live Draft) and adjusts every
 * strategy they already have, with no rebuild. The same thing can be done by
 * telling Ask JAI in chat.
 *
 * Platform-reported trades (Sleeper) are shown read-only; only the user's own
 * entries are editable.
 */

import { useEffect, useState } from 'react'
import { draftBoardApi } from '../../services/api'
import { JAI_FONTS } from '../../components/JaiBrand'
import { V2 } from '../../components/JaiV2'
import MascotDialog from '../../components/draft/MascotDialog'

const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: `1px solid ${V2.hairSoft}`, fontSize: 12.5, color: V2.navy }
const inputStyle = { fontSize: 13, fontWeight: 700, color: V2.navy, border: `1px solid ${V2.hairSoft}`, borderRadius: 8, padding: '8px 10px', fontFamily: JAI_FONTS.tight, outline: 'none', minHeight: 40, boxSizing: 'border-box' }
const smallButton = { background: 'white', color: V2.blue, border: `1px solid ${V2.hairSoft}`, borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 40 }
const removeButton = { background: 'none', border: 'none', color: V2.mute, cursor: 'pointer', fontSize: 15, padding: '6px 10px', minHeight: 36 }

const roundOf = (overall, teamCount) => Math.floor((overall - 1) / teamCount) + 1

export default function PickTradesDialog({ leagueId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState(null) // { picks, platformTradedPicks, teamCount, totalRounds, missingFields }
  const [got, setGot] = useState([])      // overall pick numbers
  const [away, setAway] = useState([])    // [{ round } | { overallPick }]
  const [gotInput, setGotInput] = useState('')
  const [awayChoice, setAwayChoice] = useState('')

  useEffect(() => {
    let alive = true
    draftBoardApi.getPickTrades(leagueId)
      .then((res) => {
        if (!alive) return
        setInfo(res.data)
        const m = res.data.manualPickTrades || {}
        setGot((m.acquired || []).map((a) => a.overallPick))
        setAway(m.tradedAway || [])
      })
      .catch(() => alive && setError('Could not load your pick trades.'))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [leagueId])

  const teamCount = info?.teamCount
  const maxOverall = teamCount && info?.totalRounds ? teamCount * info.totalRounds : null

  const addGot = () => {
    const n = Number(gotInput)
    setError('')
    if (!Number.isInteger(n) || n < 1 || (maxOverall && n > maxOverall)) {
      setError(maxOverall ? `Enter a pick number from 1 to ${maxOverall}.` : 'Enter a pick number.')
      return
    }
    if (!got.includes(n)) setGot([...got, n])
    setGotInput('')
  }

  // The picks the user could give away: every pick in their current list.
  const givableOptions = (info?.picks || []).map((p) => ({
    key: `${p.overallPick}`,
    label: `Round ${p.round}, pick ${p.pickInRound} (overall ${p.overallPick})${p.traded ? ', from a trade' : ''}`,
    // An original pick is remembered by its round (so it stays right if the draft slot changes); a pick that came from a trade by its number.
    entry: p.traded ? { overallPick: p.overallPick } : { round: p.round },
  }))
  const alreadyAway = (entry) => away.some((a) => (entry.round != null ? a.round === entry.round : a.overallPick === entry.overallPick))

  const addAway = () => {
    const opt = givableOptions.find((o) => o.key === awayChoice)
    setError('')
    if (!opt) { setError('Choose the pick you gave away.'); return }
    if (!alreadyAway(opt.entry)) setAway([...away, opt.entry])
    setAwayChoice('')
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const res = await draftBoardApi.savePickTrades(leagueId, {
        acquired: got.map((overallPick) => ({ overallPick })),
        tradedAway: away,
      })
      onSaved?.(res.data)
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save your pick trades. Try again.')
      setSaving(false)
    }
  }

  const platform = info?.platformTradedPicks
  const platformGot = platform?.acquired || []
  const platformAway = platform?.tradedAway || []

  return (
    <MascotDialog
      state="idle" size={90} width={440}
      headline="Pick trades"
      onClose={onClose} closable={!saving}
      actions={[
        { label: 'Save', primary: true, onClick: save, loading: saving, disabled: loading || (info?.missingFields || []).length > 0 },
        { label: 'Cancel', onClick: onClose, disabled: saving },
      ]}
    >
      <div style={{ textAlign: 'left', marginTop: 14 }} data-testid="pick-trades-dialog">
        {loading && <div style={{ fontSize: 12.5, color: V2.mute }}>Loading your picks…</div>}

        {!loading && (info?.missingFields || []).length > 0 && (
          <div style={{ fontSize: 12.5, color: V2.error, lineHeight: 1.5 }}>
            Confirm your league details first (draft type, teams, rounds and your first-round pick), so JAI knows how your draft order works.
          </div>
        )}

        {!loading && info && (info.missingFields || []).length === 0 && (
          <>
            <div style={{ fontSize: 12.5, color: V2.mute, lineHeight: 1.5, marginBottom: 10 }}>
              Traded draft picks? Tell JAI and your pick list, Live Draft and strategies update to match, no rebuild needed. You can also just tell Ask JAI.
            </div>

            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: V2.faint, marginBottom: 6 }}>Your picks now</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }} data-testid="pick-trades-current">
              {(info.picks || []).length === 0 && <span style={{ fontSize: 12, color: V2.mute }}>No picks</span>}
              {(info.picks || []).map((p) => (
                <span key={p.overallPick} style={{ fontSize: 11, fontWeight: 700, color: p.traded ? V2.blue : V2.mute, padding: '3px 8px 3px 0', fontFamily: JAI_FONTS.mono }}>
                  R{p.round} · {p.overallPick}{p.traded ? ' (trade)' : ''}
                </span>
              ))}
            </div>

            {(platformGot.length > 0 || platformAway.length > 0) && (
              <div style={{ fontSize: 12, color: V2.mute, lineHeight: 1.5, marginBottom: 14 }}>
                Your league's app already reports{platformGot.length ? ` an extra pick in round ${platformGot.map((p) => p.round).join(', ')}` : ''}{platformGot.length && platformAway.length ? ' and' : ''}{platformAway.length ? ` that you traded away your pick in round ${platformAway.map((p) => p.round).join(', ')}` : ''}. Those are already counted.
              </div>
            )}

            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: V2.faint, marginBottom: 4 }}>Picks you received</div>
            {got.map((n) => (
              <div key={n} style={rowStyle} data-testid="pick-trades-got-row">
                <span>Pick #{n}{teamCount ? ` (round ${roundOf(n, teamCount)})` : ''}</span>
                <button type="button" aria-label={`Remove pick ${n}`} style={removeButton} onClick={() => setGot(got.filter((x) => x !== n))}>&times;</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, margin: '8px 0 16px', flexWrap: 'wrap' }}>
              <input
                data-testid="pick-trades-got-input" type="number" inputMode="numeric" min={1} max={maxOverall || undefined}
                placeholder="Overall pick number, e.g. 25" value={gotInput}
                onChange={(e) => setGotInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addGot()}
                style={{ ...inputStyle, flex: 1, minWidth: 140 }}
              />
              <button type="button" data-testid="pick-trades-got-add" style={smallButton} onClick={addGot}>Add</button>
            </div>

            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: V2.faint, marginBottom: 4 }}>Picks you gave away</div>
            {away.map((a, i) => (
              <div key={`${a.round ?? ''}-${a.overallPick ?? ''}-${i}`} style={rowStyle} data-testid="pick-trades-away-row">
                <span>{a.round != null ? `Your round ${a.round} pick` : `Pick #${a.overallPick}`}</span>
                <button type="button" aria-label="Remove" style={removeButton} onClick={() => setAway(away.filter((_, j) => j !== i))}>&times;</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, margin: '8px 0 4px', flexWrap: 'wrap' }}>
              <select
                data-testid="pick-trades-away-select" value={awayChoice} onChange={(e) => setAwayChoice(e.target.value)}
                style={{ ...inputStyle, flex: 1, minWidth: 140, background: 'white' }}
              >
                <option value="">Choose a pick</option>
                {givableOptions.filter((o) => !alreadyAway(o.entry)).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <button type="button" data-testid="pick-trades-away-add" style={smallButton} onClick={addAway}>Add</button>
            </div>
            <div style={{ fontSize: 11.5, color: V2.faint, lineHeight: 1.5 }}>
              Pick numbers are overall picks: in a 10-team league, round 3 pick 4 is overall pick 24.
            </div>
          </>
        )}

        {error && <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: V2.error }}>{error}</div>}
      </div>
    </MascotDialog>
  )
}
